import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import type { AuthPrivateResponseDto, UserDto } from '../types/generated';
import API from '../api';

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: UserDto | null;
}

interface AuthContextValue extends State {
  platform: 'JWT';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  refresh: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: UserDto | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: UserDto;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: UserDto;
  };
};

type RefreshAction = {
  type: 'REFRESH';
  payload: {
    user: UserDto;
  };
};

type Action = InitializeAction | LoginAction | LogoutAction | RegisterAction | RefreshAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state: State, action: RegisterAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  REFRESH: (state: State, action: RefreshAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      user,
    };
  },
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken) {
          const user = await API.auth.me();

          // Check if user is active during initialization
          if (user.status === 'inactive') {
            localStorage.removeItem('accessToken');
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            return;
          }

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const { token: accessToken } = (await API.auth.login({ email, password })) as AuthPrivateResponseDto;
    localStorage.setItem('accessToken', accessToken);
    const user = await API.auth.me();

    // Check if user is active
    if (user.status === 'inactive') {
      localStorage.removeItem('accessToken');
      throw new Error('Account is inactive. Please contact your administrator.');
    }

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  };

  const logout = async (): Promise<void> => {
    localStorage.removeItem('accessToken');
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (email: string, password: string): Promise<void> => {
    const { token: accessToken } = (await API.auth.register({ email, password })) as AuthPrivateResponseDto;
    localStorage.setItem('accessToken', accessToken);
    const user = await API.auth.me();

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  const refresh = async () => {
    const user = await API.auth.me();

    // Check if user is still active during refresh
    if (user.status === 'inactive') {
      localStorage.removeItem('accessToken');
      dispatch({ type: 'LOGOUT' });
      throw new Error('Account has been deactivated. Please contact your administrator.');
    }

    dispatch({
      type: 'REFRESH',
      payload: {
        user,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        register,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
